import { Value } from 'app/types-shared';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { parseAsDateUTC, toISODatewithoutTimezone } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { DefaultFilter } from 'app/area/shared/default-filters/types-store';
import { mapFiltersToWire } from 'app/area/shared/default-filters/utils/map-filters';
import { createInitialExcelMapping, defaultFilterCompanyReportConfig } from '../constants';
import { CompanyReportItem, ExcelMappingValue, TextValue, MetadataValue, SavedReportCompanyReport } from '../types-store';
import { CompanyReportItemWire, ExcelMappingsWire, TextsWire, MetadataWire, TemplateItem } from '../types';
import { mapSavedReportsToWire } from './map-saved-reports';

export const mapCompanyReportFromWire = (wire: Partial<CompanyReportItemWire>): CompanyReportItem => ({
  id: wire?.code?.v,

  language: wire?.language?.map(codeItemToStoreValue) ?? [],

  disabled: wire?.disabled
    ? parseAsDateUTC(wire.disabled)
    : undefined,
  sequence: wire?.sequence,

  subset: wire?.subset?.map(codeItemToStoreValue) ?? [],

  usePredefDate: wire?.predefinedDate?.active ?? false,
  predefinedDate: wire?.predefinedDate?.defaultDate?.v === 0 ? undefined : codeItemToStoreValue(wire?.predefinedDate?.defaultDate),

  template: codeItemToStoreValue(wire?.template),
  defaultTemplate: codeItemToStoreValue(wire?.defaultTemplate),

  emailReport: wire?.emailSetting?.report ?? false,
  textId: wire?.emailSetting?.body?.text?.v,
  textDescription: wire?.emailSetting?.body?.text?.d,
  textSet: codeItemToStoreValue(wire?.emailSetting?.body?.textSet),

  excelMapping: excelMappingToStoreValue(wire?.excelMapping),
  texts: textsToStoreValue(wire?.text),
  metadata: metadataToStoreValue(wire?.metadata)
});

export const mapCompanyReportToWire = (
  companyReport: CompanyReportItem,
  savedReports: SavedReportCompanyReport[],
  filters: DefaultFilter[]
): CompanyReportItemWire => ({

  ...(isDefinedIncludingZero(companyReport?.id) && {
    code: {
      v: companyReport.id
    }
  }),

  language: companyReport?.language?.map(languageItem => ({
    v: languageItem.value,
    d: languageItem.description?.trim()
  })) ?? [],

  disabled: companyReport?.disabled
    ? toISODatewithoutTimezone(companyReport.disabled)()
    : undefined,

  sequence: companyReport?.sequence,

  subset: companyReport?.subset?.map(subset => ({
    v: subset.value
  })) ?? [],

  predefinedDate: {
    active: companyReport?.usePredefDate,
    defaultDate: {
      v: companyReport?.predefinedDate?.value,
      d: companyReport?.predefinedDate?.description
    }
  },

  template: isDefinedIncludingZero(companyReport?.template?.value)
    ? { v: companyReport?.template?.value, d: companyReport?.template?.description }
    : {} as Value,
  defaultTemplate: isDefinedIncludingZero(companyReport?.defaultTemplate?.value)
    ? { v: companyReport?.defaultTemplate?.value, d: companyReport?.defaultTemplate?.description }
    : {} as Value,

  emailSetting: {
    report: companyReport?.emailReport,
    body: isDefinedIncludingZero(companyReport?.textId) ? {
      text: { v: companyReport?.textId },
      textSet: { v: companyReport?.textSet?.value }
    } : undefined
  },
  excelMapping: excelMappingToWire(companyReport?.excelMapping),
  savedReport: mapSavedReportsToWire(savedReports),
  filter: mapFiltersToWire(filters, defaultFilterCompanyReportConfig),
  text: mapTextsToWire(companyReport?.texts),
  metadata: mapMetadataToWire(companyReport?.metadata)
});

export const excelMappingToStoreValue = (excelMappings: ExcelMappingsWire): ExcelMappingValue[] =>
  excelMappings ? Object.keys(excelMappings).map(id => ({
    id,
    cell: excelMappings?.[id]?.cell ?? '',
    range: excelMappings?.[id]?.range ?? ''
  })) : createInitialExcelMapping;

export const excelMappingToWire = (excelMappings: ExcelMappingValue[]) =>
  excelMappings?.reduce(
    (obj, item) => ({
      ...obj, [item.id]: { cell: item.cell?.trimEnd(), range: item.range?.trim() }
    }), {});

export const textsToStoreValue = (texts: TextsWire[]): TextValue[] =>
  texts ? texts?.map((text, index) => ({
    id: index + 1,
    textId: text?.text?.v,
    textSet: codeItemToStoreValue(text?.textSet),
    textDescription: text?.text?.d,

    cell: text?.details?.cell,
    range: text?.details?.range
  })) : [];

export const mapTextsToWire = (texts: TextValue[]): TextsWire[] =>
  texts?.map(text => ({
    text: { v: text.textId },
    textSet: { v: text.textSet.value, d: text.textSet.description },
    details: {
      cell: text.cell?.trimEnd(),
      range: text.range?.trim()
    }
  })
  ) ?? [];

export const metadataToStoreValue = (metadata: MetadataWire[]): MetadataValue[] =>
  metadata ? metadata?.map((meta, index) => ({
    id: index + 1,
    metadataType: codeItemToStoreValue(meta?.metadataType),
    cell: meta?.details?.cell,
    range: meta?.details?.range
  })) : [];

export const mapMetadataToWire = (metadata: MetadataValue[]): MetadataWire[] =>
  metadata?.map(meta => ({
    metadataType: { v: meta.metadataType.value, d: meta.metadataType.description },
    details: { cell: meta.cell?.trimEnd(), range: meta.range?.trim() }
  })
  ) ?? [];

export const createExcelMappingFieldId = (id: string, fieldName: string) => `${id}_${fieldName}`;

export const mapTemplateFromWire = (data: { id: number; description: string }): TemplateItem => ({
  template: { value: data.id, description: data.description }
});
